import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '../../components/AppBar';
import Footer from '../../components/Footer';
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { APP_CONFIG } from '../../config/appConfig';
import { Link } from 'react-router-dom';
import './ForgotPassword.css';

const SESSION_KEY = 'pwd_reset_session';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [step, setStep] = useState('request'); // 'request' | 'reset' | 'done'

  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const BASE_URL = APP_CONFIG.API_BASE_URL;

  // Restore session from sessionStorage on mount (in case of page refresh)
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const { savedSessionId, savedEmail } = JSON.parse(saved);
        if (savedSessionId && savedEmail) {
          setSessionId(savedSessionId);
          setEmail(savedEmail);
          setStep('reset');
          setInfoMessage('A code was already sent to your email.');
        }
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
  }, []);

  const passwordsMatch = confirmPassword === '' || newPassword === confirmPassword;
  const passwordLongEnough = newPassword.length === 0 || newPassword.length >= 8;

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfoMessage('');

    try {
      const res = await fetch(`${BASE_URL}/forgot-password/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok) {
        setSessionId(data.sessionId);
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ savedSessionId: data.sessionId, savedEmail: email })
        );
        setStep('reset');
        setInfoMessage(data.message || 'Check your email for the 6-digit code.');
      } else {
        setError(data.error || 'Something went wrong.');
      }
    } catch {
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, otp, newPassword, confirmPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        sessionStorage.removeItem(SESSION_KEY);
        setStep('done');
      } else {
        setError(data.error || 'Something went wrong.');
      }
    } catch {
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="fp-page">
        <AppBar />
        <div className="fp-wrapper">
          <div className="fp-container">

            {step === 'request' && (
              <>
                <h2 className="fp-heading">Reset Password</h2>
                <hr className="fp-divider" />
                {error && <p className="fp-error">{error}</p>}

                <form onSubmit={handleRequest} className="fp-form">
                  <label className="fp-label">Email</label>
                  <div className="fp-input-with-icon">
                    <MdEmail className="fp-input-icon" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="fp-input"
                      required
                    />
                  </div>

                  <button type="submit" className="fp-button" disabled={loading}>
                    Send Code
                  </button>
                </form>

                <p className="fp-back-link">
                  <Link to="/login">Back to Login</Link>
                </p>
              </>
            )}

            {step === 'reset' && (
              <>
                <h2 className="fp-heading">Enter Code</h2>
                <hr className="fp-divider" />
                {infoMessage && <p className="fp-info">{infoMessage}</p>}
                {error && <p className="fp-error">{error}</p>}

                <form onSubmit={handleReset} className="fp-form">
                  <label className="fp-label">Verification Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="6-digit code"
                    className="fp-input"
                    maxLength={6}
                    required
                  />

                  <label className="fp-label">New Password</label>
                  <div className="fp-input-with-icon">
                    <MdLock className="fp-input-icon" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password"
                      className={`fp-input fp-input-padded-right${!passwordLongEnough ? ' fp-input-error-border' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      className="fp-eye-icon"
                      onClick={() => setShowNewPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  {!passwordLongEnough && (
                    <p className="fp-field-error">Password must be at least 8 characters.</p>
                  )}

                  <label className="fp-label">Confirm Password</label>
                  <div className="fp-input-with-icon">
                    <MdLock className="fp-input-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className={`fp-input fp-input-padded-right${!passwordsMatch ? ' fp-input-error-border' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      className="fp-eye-icon"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  {!passwordsMatch && (
                    <p className="fp-field-error">Passwords do not match.</p>
                  )}

                  <button
                    type="submit"
                    className="fp-button"
                    disabled={loading || !passwordsMatch || !passwordLongEnough}
                  >
                    Reset Password
                  </button>
                </form>

                <p className="fp-back-link">
                  <span
                    className="fp-back-link-span"
                    onClick={() => {
                      sessionStorage.removeItem(SESSION_KEY);
                      setStep('request');
                      setOtp('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setError('');
                      setInfoMessage('');
                    }}
                  >
                    Use a different email
                  </span>
                </p>
              </>
            )}

            {step === 'done' && (
              <>
                <h2 className="fp-heading">Password Reset</h2>
                <hr className="fp-divider" />
                <p className="fp-success">Your password has been updated successfully.</p>
                <button className="fp-button" onClick={() => navigate('/login')}>
                  Go to Login
                </button>
              </>
            )}

          </div>
        </div>

        {loading && (
          <div className="fp-loading-overlay">
            <div className="fp-spinner"></div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPassword;